import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const SearchModal = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);

  const trendingDestinations = [
    { id: 1, name: 'Boracay', type: 'Beach Paradise', icon: 'Waves' },
    { id: 2, name: 'Palawan', type: 'Island Adventure', icon: 'Mountain' },
    { id: 3, name: 'Manila', type: 'City Experience', icon: 'Building2' },
    { id: 4, name: 'Cebu', type: 'Cultural Hub', icon: 'Landmark' },
    { id: 5, name: 'Siargao', type: 'Surf Paradise', icon: 'Waves' },
    { id: 6, name: 'Baguio', type: 'Mountain Retreat', icon: 'Mountain' },
  ];

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      inputRef?.current?.focus();
    }

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (searchQuery?.trim()) {
      const filtered = trendingDestinations?.filter(dest =>
        dest?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        dest?.type?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const handleSearch = (destination) => {
    const newSearch = {
      id: Date.now(),
      name: destination?.name,
      type: destination?.type,
      icon: destination?.icon,
    };

    setRecentSearches(prev => {
      const filtered = prev?.filter(item => item?.name !== destination?.name);
      return [newSearch, ...filtered]?.slice(0, 5);
    });

    setSearchQuery('');
    onClose();
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
  };

  if (!isOpen) return null;

  return (
    <div className="search-modal-overlay" onClick={onClose}>
      <div className="search-modal-container" onClick={(e) => e?.stopPropagation()}>
        <div className="search-modal-header">
          <div className="search-modal-input-wrapper">
            <Icon name="Search" size={20} className="search-modal-icon" />
            <input
              ref={inputRef}
              type="text"
              className="search-modal-input"
              placeholder="Search destinations, activities, or experiences..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {searchQuery?.trim() ? (
          <div className="search-modal-section">
            <h3 className="search-modal-section-title">
              Search Results ({searchResults?.length})
            </h3>
            <div className="search-modal-results">
              {searchResults?.length > 0 ? (
                searchResults?.map((destination) => (
                  <button
                    key={destination?.id}
                    className="search-modal-result-item"
                    onClick={() => handleSearch(destination)}
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name={destination?.icon} size={24} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-foreground">{destination?.name}</p>
                      <p className="text-sm text-muted-foreground">{destination?.type}</p>
                    </div>
                    <Icon name="ArrowRight" size={20} className="text-muted-foreground" />
                  </button>
                ))
              ) : (
                <div className="text-center py-12">
                  <Icon name="SearchX" size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <p className="text-foreground font-medium mb-2">No results found</p>
                  <p className="text-sm text-muted-foreground">
                    Try searching for different destinations or activities
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {recentSearches?.length > 0 && (
              <div className="search-modal-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="search-modal-section-title">Recent Searches</h3>
                  <button
                    onClick={clearRecentSearches}
                    className="text-sm text-primary hover:underline"
                  >
                    Clear all
                  </button>
                </div>
                <div className="search-modal-results">
                  {recentSearches?.map((search) => (
                    <button
                      key={search?.id}
                      className="search-modal-result-item"
                      onClick={() => handleSearch(search)}
                    >
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                        <Icon name={search?.icon} size={24} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-semibold text-foreground">{search?.name}</p>
                        <p className="text-sm text-muted-foreground">{search?.type}</p>
                      </div>
                      <Icon name="ArrowRight" size={20} className="text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="search-modal-section">
              <h3 className="search-modal-section-title">Trending Destinations</h3>
              <div className="search-modal-results">
                {trendingDestinations?.map((destination) => (
                  <button
                    key={destination?.id}
                    className="search-modal-result-item"
                    onClick={() => handleSearch(destination)}
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name={destination?.icon} size={24} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-foreground">{destination?.name}</p>
                      <p className="text-sm text-muted-foreground">{destination?.type}</p>
                    </div>
                    <Icon name="TrendingUp" size={20} className="text-primary" />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SearchModal;